import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { BoldIcon, ItalicIcon, LinkIcon, ListIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@r/components/ui/button.tsx';
import { Input } from '@r/components/ui/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';
import { Toggle } from '@r/components/ui/toggle.tsx';
import { cn } from '@r/utils/utils.ts';

export interface RichTextEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * Éditeur de texte enrichi minimal (Tiptap) : gras, italique, liste à puces, lien.
 * Émet du HTML via `onChange` — vide (`''`) quand le contenu est vide.
 * Le rendu doit être assaini (DOMPurify) avant affichage.
 */
const EXTENSIONS = [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    orderedList: false,
    underline: false,
    link: {
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' }
    }
  })
];

export function RichTextEditor({
  value,
  onChange,
  onBlur,
  disabled = false,
  error = false,
  className,
  ariaLabel
}: RichTextEditorProps) {
  const { t } = useTranslation();
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value ?? '',
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
        class: cn(
          'min-h-32 w-full px-3 py-2 text-sm font-light leading-relaxed focus:outline-none',
          '[&_p]:my-0 [&_p:not(:first-child)]:mt-2',
          '[&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-0.5',
          '[&_a]:text-primary [&_a]:underline'
        )
      }
    },
    onUpdate: ({ editor }) => onChange?.(editor.isEmpty ? '' : editor.getHTML()),
    onBlur: () => onBlur?.()
  });

  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor?.isActive('bold') ?? false,
      italic: editor?.isActive('italic') ?? false,
      bulletList: editor?.isActive('bulletList') ?? false,
      link: editor?.isActive('link') ?? false
    })
  });

  // Resynchronise si la valeur externe change (reset RHF, ouverture en édition).
  useEffect(() => {
    if (!editor) return;
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (value !== undefined && value !== current) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor || !state) return null;

  const applyLink = () => {
    const url = linkUrl.trim();
    const chain = editor.chain().focus().extendMarkRange('link');
    if (url) {
      chain.setLink({ href: url }).run();
    } else {
      chain.unsetLink().run();
    }
    setLinkOpen(false);
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-input bg-background',
        'focus-within:border-primary',
        error && 'border-red-400',
        disabled && 'cursor-not-allowed opacity-30',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-input p-1.5">
        <Toggle
          size="sm"
          pressed={state.bold}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          aria-label={t('rich.text.editor.bold')}
        >
          <BoldIcon />
        </Toggle>
        <Toggle
          size="sm"
          pressed={state.italic}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          aria-label={t('rich.text.editor.italic')}
        >
          <ItalicIcon />
        </Toggle>
        <Toggle
          size="sm"
          pressed={state.bulletList}
          onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          aria-label={t('rich.text.editor.bullet.list')}
        >
          <ListIcon />
        </Toggle>

        <Popover
          open={linkOpen}
          onOpenChange={(open) => {
            if (open) setLinkUrl((editor.getAttributes('link').href as string) ?? '');
            setLinkOpen(open);
          }}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              base="default"
              variant={state.link ? 'secondary' : 'ghost'}
              size="custom_icon"
              disabled={disabled}
              aria-label={t('rich.text.editor.link')}
            >
              <LinkIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-72 space-y-2">
            <Input
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
              placeholder="https://…"
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                  event.preventDefault();
                  applyLink();
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                base="default"
                variant="ghost"
                size="sm"
                onClick={() => setLinkOpen(false)}
              >
                {t('rich.text.editor.link.cancel')}
              </Button>
              <Button type="button" base="default" size="sm" onClick={applyLink}>
                {t('rich.text.editor.link.apply')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
